// src/client/renderer/vfx/audio-hooks.ts
import type { AudioManager, SfxId } from '../../audio';
import type { AudioCueId, AudioSyncHooks, ParticlePresetId } from './types';

/** No-op fallback for tests and VFX paths mounted before the runtime audio manager unlocks. */
export const NO_OP_AUDIO_HOOKS: AudioSyncHooks = {
  onAudioCue: () => {},
};

export type AudioManagerProvider = () => Pick<AudioManager, 'playSfx'> | null;

export const AUDIO_CUE_TO_SFX_ID: Readonly<Partial<Record<AudioCueId, SfxId>>> = {
  'sfx.hit-impact': 'sfx-combat-hit-heavy',
  'sfx.jump-dust': 'sfx-movement-jump',
  'sfx.death-dissolve': 'sfx-sting-defeat',
  'sfx.jump': 'sfx-movement-jump',
  'sfx.hop': 'sfx-movement-step-soft',
  'sfx.hit': 'sfx-combat-hit-light',
  'sfx.death': 'sfx-sting-defeat',
  'sfx.respawn': 'sfx-misc-ding-positive',
  'sfx.victory': 'sfx-fanfare-victory',
  'sfx.defeat': 'sfx-sting-defeat',
  'sfx.dice_throw': 'sfx-dice-roll',
  'sfx.item_thunder_fist': 'sfx-items-thunder-fist-cast',
  'sfx.item_frost_blaster': 'sfx-items-frost-blaster-shoot',
  'sfx.item_meteor_strike': 'sfx-items-meteor-strike-whistle',
  'sfx.item_orbital_cannon': 'sfx-items-orbital-cannon-charge',
  'sfx.item_toxic_cloud': 'sfx-items-toxic-cloud-disperse',
  'sfx.item_gravity_well': 'sfx-items-gravity-well-cast',
  'sfx.item_lightning_dash': 'sfx-items-lightning-dash-zip',
  'sfx.item_phoenix_potion': 'sfx-items-phoenix-potion-revive',
  'sfx.item_stone_skin': 'sfx-items-stone-skin-activate',
  'sfx.item_dimension_rift': 'sfx-items-dimension-rift-open',
  'sfx.item_mystery_box': 'sfx-items-mystery-box-open',
  'sfx.item_shadow_stalker': 'sfx-items-shadow-stalker-summon',
  'sfx.item_cursed_relic': 'sfx-items-cursed-relic-pulse',
  'sfx.item_soul_duel': 'sfx-items-soul-duel-portal',
  'sfx.awards_intro': 'sfx-misc-ding-positive',
  'sfx.award_reveal': 'sfx-confetti-pop',
  'sfx.trophy_shine': 'sfx-misc-ding-positive',
  'sfx.fanfare_finale': 'sfx-fanfare-victory',
} as const;

export function resolveSfxIdForAudioCue(cueId: AudioCueId): SfxId | null {
  return AUDIO_CUE_TO_SFX_ID[cueId] ?? null;
}

export function createAudioCueHooks(getManager: AudioManagerProvider): AudioSyncHooks {
  return {
    onAudioCue(cueId): void {
      const sfxId = resolveSfxIdForAudioCue(cueId);
      if (sfxId == null) return;
      getManager()?.playSfx(sfxId);
    },
  };
}

/** Derive the generic audio cue id from a particle preset id. */
export function audioCueForPreset(presetId: ParticlePresetId): AudioCueId {
  return `sfx.${presetId}`;
}
